import { KillHistoryEntry } from "../types/KillHistoryEntry";

/**
 * Use mock kill history until a backend kill index is available.
 * Set to `false` to load from the API instead.
 */
export const MOCK_KILL_HISTORY_ENABLED = true;

export enum KillHistoryResultType {
  SingleKill = 0,
  DoubleKill = 1,
  BackfireKill = 2,
}

export const KILL_HISTORY_RESULT_LABELS: Record<KillHistoryResultType, string> = {
  [KillHistoryResultType.SingleKill]: "Single kill",
  [KillHistoryResultType.DoubleKill]: "Double kill",
  [KillHistoryResultType.BackfireKill]: "Backfire kill",
};

export const getKillHistoryResultLabel = (
  resultType: KillHistoryResultType
): string =>
  KILL_HISTORY_RESULT_LABELS[resultType] ?? "Successful kill";

/** Labels for the two distinct bullet figures shown in a kill row. */
export const KILL_HISTORY_BULLET_LABELS = {
  attacker: "Attacker",
  backfire: "Backfire",
} as const;

export type KillHistoryBulletKind = keyof typeof KILL_HISTORY_BULLET_LABELS;

export interface KillHistoryBulletLine {
  kind: KillHistoryBulletKind;
  label: string;
  value: number;
}

/**
 * Resolve the bullet figures to display for a kill, disambiguating the
 * attacker's spend from the defender's backfire return fire.
 *
 * - Single kill: only the attacker's bullets are relevant.
 * - Backfire kill / Double kill: the attacker spent bullets AND the defender's
 *   backfire returned bullets (the latter is what kills the attacker), so both
 *   figures are shown and labeled.
 *
 * Falls back to the legacy single `bullets` value (treated as the attacker's
 * spend) when the explicit split is not provided.
 */
export const getKillHistoryBulletLines = (
  entry: KillHistoryEntry
): KillHistoryBulletLine[] => {
  const attackerBullets = entry.attackerBullets ?? entry.bullets;
  const backfireBullets = entry.backfireBullets;

  const lines: KillHistoryBulletLine[] = [];

  if (attackerBullets !== undefined) {
    lines.push({
      kind: "attacker",
      label: KILL_HISTORY_BULLET_LABELS.attacker,
      value: attackerBullets,
    });
  }

  const showsBackfire =
    entry.resultType === KillHistoryResultType.BackfireKill ||
    entry.resultType === KillHistoryResultType.DoubleKill;

  if (showsBackfire && backfireBullets !== undefined) {
    lines.push({
      kind: "backfire",
      label: KILL_HISTORY_BULLET_LABELS.backfire,
      value: backfireBullets,
    });
  }

  return lines;
};

/** Format a bullet count with thousands separators (no currency). */
export const formatKillHistoryBullets = (value: number): string =>
  value.toLocaleString("en-US");

export type KillHistoryPlayerRole = "attacker" | "victim";

/** Strikethrough: dead profile, or killed per result type (single→victim, double→both, backfire→attacker). */
export const shouldCrossKillHistoryPlayer = (
  entry: KillHistoryEntry,
  role: KillHistoryPlayerRole
): boolean => {
  const isAttacker = role === "attacker";

  if (isAttacker ? entry.attackerIsDead : entry.victimIsDead) {
    return true;
  }

  switch (entry.resultType) {
    case KillHistoryResultType.SingleKill:
      return !isAttacker;
    case KillHistoryResultType.DoubleKill:
      return true;
    case KillHistoryResultType.BackfireKill:
      return isAttacker;
    default:
      return false;
  }
};

const hoursAgo = (hours: number): number =>
  Math.floor(Date.now() / 1000) - hours * 3600;

/** Sample successful kills for UI development (single, double, backfire). */
export const MOCK_KILL_HISTORY_ENTRIES: KillHistoryEntry[] = [
  {
    id: "mock-kill-1",
    timestamp: hoursAgo(2),
    attackerAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    victimAddress: "0x8ba1f109551bD432803012645Ac136c22C177e9",
    attackerName: "AldoVescari",
    victimName: "PaulieWalnuts",
    resultType: KillHistoryResultType.SingleKill,
    victimIsDead: true,
    bullets: 12500,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000001",
    blockNumber: 42100100,
  },
  {
    id: "mock-kill-2",
    timestamp: hoursAgo(8),
    attackerAddress: "0x3f5CE5FBFe3E9af3971dD823D1523B0aC24c131a",
    victimAddress: "0x1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c4d5",
    attackerName: "VitoCorleone",
    victimName: "Salvatore",
    resultType: KillHistoryResultType.DoubleKill,
    attackerIsDead: true,
    victimIsDead: true,
    bullets: 48000,
    attackerBullets: 48000,
    backfireBullets: 96000,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000002",
    blockNumber: 42099850,
  },
  {
    id: "mock-kill-3",
    timestamp: hoursAgo(14),
    attackerAddress: "0x5aAeb6053F3E94C9b9A09f33669435E7Ef1BeAed",
    victimAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    attackerName: "NicoBellandi",
    victimName: "AldoVescari",
    resultType: KillHistoryResultType.BackfireKill,
    attackerIsDead: true,
    bullets: 22000,
    attackerBullets: 22000,
    backfireBullets: 44000,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000003",
    blockNumber: 42099500,
  },
  {
    id: "mock-kill-4",
    timestamp: hoursAgo(26),
    attackerAddress: "0x8626f6940E2eb28930eFb4CeF49B2d1F2C9C1199",
    victimAddress: "0x2546BcD3c84621e976D8185Aa27800a256bD1eC3",
    attackerName: "Silvio",
    victimName: "Adriana",
    resultType: KillHistoryResultType.SingleKill,
    victimIsDead: true,
    bullets: 8500,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000004",
    blockNumber: 42098800,
  },
  {
    id: "mock-kill-5",
    timestamp: hoursAgo(36),
    attackerAddress: "0x1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c4d5",
    victimAddress: "0x3f5CE5FBFe3E9af3971dD823D1523B0aC24c131a",
    attackerName: "Salvatore",
    victimName: "VitoCorleone",
    resultType: KillHistoryResultType.BackfireKill,
    attackerIsDead: true,
    bullets: 95000,
    attackerBullets: 95000,
    backfireBullets: 190000,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000005",
    blockNumber: 42098100,
  },
  {
    id: "mock-kill-6",
    timestamp: hoursAgo(52),
    attackerAddress: "0x8ba1f109551bD432803012645Ac136c22C177e9",
    victimAddress: "0x5aAeb6053F3E94C9b9A09f33669435E7Ef1BeAed",
    attackerName: "PaulieWalnuts",
    victimName: "NicoBellandi",
    resultType: KillHistoryResultType.DoubleKill,
    attackerIsDead: true,
    victimIsDead: true,
    bullets: 60000,
    attackerBullets: 60000,
    backfireBullets: 120000,
    transactionHash: "0xmock00000000000000000000000000000000000000000000000000000000000006",
    blockNumber: 42097200,
  },
];
